import { useCallback, useState } from "react";
import type {
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  UseFormRegister,
  Validate,
} from "react-hook-form";
import { Box, FormControl, Skeleton, styled, TextField } from "@mui/material";
import type { NumericInputType } from "@utils";
import { parseNumericInputValueToString, parseStringToNumericInputValue } from "@utils";

const StyledTextField = styled(TextField)(() => ({
  "& input": {
    minHeight: "32px",
    textAlign: "left",
    padding: "12px",
  },
}));

const InputLabel = styled("label")(({ theme }) => ({
  fontWeight: 500,
  paddingBottom: "4px",
  color: theme.palette.text.primary,
}));

const ItalicLabel = styled("span")({
  fontStyle: "italic",
  fontWeight: 400,
});

const InputExample = styled("span")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  fontSize: "12px",
  color: theme.palette.text.secondary,
  paddingTop: "4px",
}));

const RelativeContainer = styled(Box)({
  position: "relative",
});

export const ErrorMessage = styled("span")(({ theme }) => ({
  display: "block",
  fontSize: "12px",
  color: theme.palette.error.main,
}));

const UnitsSpan = styled("span")(({ theme }) => ({
  position: "absolute",
  right: "2.5%",
  bottom: "14px",
  color: theme.palette.text.secondary,
}));

interface InputProp<TFieldValues extends FieldValues, TName extends Path<TFieldValues>> {
  title?: string;
  parameterName: TName;
  errorText?: string;
  showOptionalLabel?: boolean;
  register: UseFormRegister<TFieldValues>;
  // Read by the field's name, as a key of the form's errors.
  errors: FieldErrors;
  description?: string | React.ReactNode;
  placeholder?: string;
  inputType?: "text" | "number" | "email" | "password";
  numericInputType?: NumericInputType;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  validate?: Validate<PathValue<TFieldValues, TName>, TFieldValues>;
  units?: string;
  autoComplete?: string;
  obscureInTracker?: boolean;
}

interface NumericInputProps {
  numericExternalValue?: number | string;
  handleNumericExternalValueChange?: (value: number | undefined) => void;
  numericMaxValue?: number;
  numericMinValue?: number;
}

export const SingleFormInput = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  title,
  parameterName,
  errorText,
  showOptionalLabel,
  register,
  errors,
  description,
  placeholder,
  inputType = "text",
  numericInputType,
  onChange,
  onKeyDown,
  onBlur,
  validate,
  units,
  autoComplete,
  numericExternalValue,
  handleNumericExternalValueChange,
  numericMaxValue = 999999999,
  numericMinValue = 0,
  obscureInTracker = false,
}: InputProp<TFieldValues, TName> & NumericInputProps) => {
  const isNumberType = inputType === "number";

  const [numericInternalValue, setNumericInternalValue] = useState<string>(
    numericExternalValue?.toString() ?? "",
  );

  const [prevNumericExternalValue, setPrevNumericExternalValue] =
    useState(numericExternalValue);

  // Update internal value when external value changes, during render instead of in an
  // effect, so the input never shows the old value. Object.is, as the effect's
  // dependency check did: NaN equals NaN.
  if (!Object.is(numericExternalValue, prevNumericExternalValue)) {
    setPrevNumericExternalValue(numericExternalValue);
    setNumericInternalValue(numericExternalValue?.toString() ?? "");
  }

  const handleNumericInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!numericInputType || !handleNumericExternalValueChange) return;

      const stringValue = parseNumericInputValueToString(
        event.target.value,
        numericInputType,
      );

      if (stringValue === "") return setNumericInternalValue("");

      const numericValue = parseStringToNumericInputValue(stringValue, numericInputType);

      if (
        numericInputType === "document_number" ||
        (numericMinValue <= numericValue && numericValue <= numericMaxValue)
      ) {
        handleNumericExternalValueChange(numericValue);

        return setNumericInternalValue(stringValue);
      }
    },
    [
      numericInputType,
      numericMaxValue,
      numericMinValue,
      handleNumericExternalValueChange,
    ],
  );

  const onNumericInputBlur = useCallback(() => {
    if (!numericInputType || !handleNumericExternalValueChange || !numericInternalValue)
      return;

    const numericValue = parseStringToNumericInputValue(
      numericInternalValue,
      numericInputType,
    );

    handleNumericExternalValueChange(numericValue);
  }, [numericInputType, handleNumericExternalValueChange, numericInternalValue]);

  return (
    <FormControl fullWidth>
      {title && (
        <InputLabel>
          {title} {showOptionalLabel && <ItalicLabel>(Opcional)</ItalicLabel>}
        </InputLabel>
      )}
      <StyledTextField
        type={isNumberType ? "text" : inputType}
        placeholder={placeholder}
        variant="outlined"
        error={!!errors[parameterName]}
        {...register(parameterName, {
          required: errorText ?? false,
          onChange: onChange,
          onBlur: onBlur,
          validate: validate,
        })}
        onKeyDown={onKeyDown}
        inputProps={{
          autoComplete: autoComplete ?? "on",
          inputMode: isNumberType
            ? numericInputType === "decimal"
              ? "decimal"
              : "numeric"
            : "text",
          "data-openreplay-obscured": obscureInTracker ? "" : undefined,
        }}
        {...(isNumberType && {
          value: numericInternalValue,
          onChange: handleNumericInputChange,
          onBlur: onNumericInputBlur,
        })}
      />

      <RelativeContainer>
        <UnitsSpan>{units}</UnitsSpan>

        {!!errors[parameterName] && (
          <ErrorMessage>
            {errors[parameterName]?.message?.toString() ?? "Este item es obligatorio"}
          </ErrorMessage>
        )}
      </RelativeContainer>

      {description && typeof description === "string" ? (
        <InputExample>{description}</InputExample>
      ) : (
        description
      )}
    </FormControl>
  );
};

const SkeletonContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  width: "100%",
});

export const SingleFormInputSkeleton: React.FC = () => {
  return (
    <SkeletonContainer>
      <Skeleton variant="rectangular" height={23} width="30%" />
      <Skeleton variant="rectangular" height={56} width="100%" />
    </SkeletonContainer>
  );
};
